import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, useMediaQuery, useTheme,
  Badge, Tooltip, Divider, Collapse, Avatar, Menu, MenuItem,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ForumIcon from '@mui/icons-material/Forum';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CommandPalette from './CommandPalette';
import NotificationsPopover from './NotificationsPopover';
import DnsIcon from '@mui/icons-material/Dns';
import GavelIcon from '@mui/icons-material/Gavel';
import BugReportIcon from '@mui/icons-material/BugReport';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon2 from '@mui/icons-material/Psychology';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HubIcon from '@mui/icons-material/Hub';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

const HUB_URL = 'http://24.199.87.96:3005';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 60;

const menuItems = [
  { divider: true, label: 'Clínica' },
  { text: 'Agenda de Citas', icon: <CalendarMonthIcon />, path: '/agenda', badgeKey: 'citas', shortcut: 'a', quickAdd: '/agenda?new=true', quickAddLabel: 'Nueva cita' },
  { text: 'Inbox', icon: <InboxRoundedIcon />, path: '/inbox', badgeKey: 'tareas', badgeColor: 'error', shortcut: 'i', quickAdd: '/inbox?new=true', quickAddLabel: 'Nueva tarea' },
  { text: 'Expedientes', icon: <LocalHospitalIcon />, path: '/emr', shortcut: 'e', quickAdd: '/emr?new=true', quickAddLabel: 'Nuevo paciente' },
  { divider: true, label: 'Operación' },
  { text: 'Conversaciones', icon: <ForumIcon />, path: '/conversaciones', badgeKey: 'conversaciones', shortcut: 'o' },
  { text: 'Smart Flows', icon: <AutoAwesomeIcon />, path: '/smart-flows', shortcut: 's' },
  { text: 'Analiticas', icon: <AnalyticsIcon />, path: '/analytics', shortcut: 'y' },
  { divider: true, label: 'Negocio' },
  { text: 'Finanzas', icon: <AccountBalanceWalletIcon />, path: '/finanzas', shortcut: 'f' },
  { text: 'Catalogo', icon: <ReceiptIcon />, path: '/catalogo' },
  { text: 'Mi Suscripcion', icon: <CardMembershipIcon />, path: '/suscripcion', shortcut: 'm' },
  { divider: true, label: 'Pruebas' },
  { text: 'Plantillas voz', icon: <RecordVoiceOverRoundedIcon />, path: '/plantillas-voz', shortcut: 'v' },
];

// Sección Admin completa: solo equipo NARA Tech (company_id = 4).
// Cuando los clientes tengan su propio "admin de clínica" (operación interna
// de cada consultorio), se separará en otra sección controlada por el rol
// dentro de la empresa cliente.
const adminItems = [
  { text: 'Consumo Servicios', icon: <DataUsageIcon />, path: '/consumo-tokens' },
  { text: 'Planes', icon: <AdminPanelSettingsIcon />, path: '/admin/planes' },
  { text: 'Servicios', icon: <DnsIcon />, path: '/admin/servicios' },
  { text: 'Politicas', icon: <GavelIcon />, path: '/admin/politicas' },
  {
    text: 'Catálogos',
    icon: <CategoryIcon />,
    // path se usa solo para detectar "estoy dentro de este grupo" y auto-expandir
    path: '/admin/catalogo-',
    children: [
      { text: 'Estudios', icon: <ScienceIcon />, path: '/admin/catalogo-estudios' },
      { text: 'Medicamentos', icon: <MedicationIcon />, path: '/admin/catalogo-medicamentos' },
      { text: 'Plantillas de chatbot', icon: <MenuBookRoundedIcon />, path: '/admin/catalogo-plantillas' },
    ],
  },
  { text: 'Actuar como', icon: <SwapHorizIcon />, path: '/admin/impersonate' },
  { text: 'Auditoría imp.', icon: <HistoryIcon />, path: '/admin/impersonate/audit', badgeKey: 'impersonation', badgeColor: 'warning' },
  { text: 'Tests', icon: <BugReportIcon />, path: '/admin/tests', badgeKey: 'tests', badgeColor: 'error' },
  { text: 'Correcciones IA', icon: <PsychologyIcon />, path: '/admin/corrections' },
  { text: 'Base de Datos', icon: <StorageIcon />, path: '/admin/db' },
  { text: 'Design Studio', icon: <CodeIcon />, path: '/admin/design-studio' },
  { text: 'Clasificador', icon: <PsychologyIcon2 />, path: '/admin/clasificador' },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPlataformaAdmin, setIsPlataformaAdmin] = useState(false);  // company_id === 4 (NARA Tech)
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('');
  const [empresaName, setEmpresaName] = useState('');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [badges, setBadges] = useState({ conversaciones: 0, citas: 0 });
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);  // path del item hovered
  const [gMode, setGMode] = useState(false);  // "g X" keyboard mode activo
  // Últimas páginas visitadas (paths) — persiste en localStorage
  const [recentPaths, setRecentPaths] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_recents') || '[]'); }
    catch { return []; }
  });
  // Favoritos pineados (paths) — persiste en localStorage
  const [favoritePaths, setFavoritePaths] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_favorites') || '[]'); }
    catch { return []; }
  });
  const isFavorite = (path) => favoritePaths.includes(path);
  const toggleFavorite = (path) => {
    setFavoritePaths((prev) => {
      const next = prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path].slice(0, 6);  // máx 6 favoritos
      try { localStorage.setItem('sidebar_favorites', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // Grupos del sidebar (con children) que están abiertos. Persisten en localStorage.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_open_groups') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (key) => setOpenGroups((g) => {
    const next = { ...g, [key]: !g[key] };
    try { localStorage.setItem('sidebar_open_groups', JSON.stringify(next)); } catch {}
    return next;
  });

  const drawerWidth = isMobile ? DRAWER_WIDTH : (collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUserName(u.displayName || 'Usuario');
        setUserEmail(u.email || '');
        try {
          const res = await fetch(`/api/users/by_uid/${u.uid}`);
          const data = await res.json();
          setIsSuperAdmin(data?.role === 'superadmin');
          // Nombre real desde BD si está disponible (más confiable que displayName)
          if (data?.name) {
            const fullName = `${data.name} ${data.lastname || ''}`.trim();
            setUserName(fullName);
          }
          setUserRole(data?.role || '');
          setEmpresaName(data?.empresa?.company || '');
          // Empresa NARA Tech (id=4) → ven la sección "Plataforma" del menú admin.
          // El campo viene como número (data.company_id) o como objeto (data.empresa.id).
          const empresaId = data?.company_id ?? data?.empresa?.id;
          setIsPlataformaAdmin(String(empresaId) === '4');
        } catch (e) {
          console.error("Error cargando perfil:", e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchBadges = useCallback(async () => {
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      const r = await fetch('/api/sidebar-counts', {
        headers: { 'X-User-Id': String(userId) },
      });
      if (!r.ok) return;
      const d = await r.json();
      setBadges({
        // Mapeo backend → keys que usan los items del menú
        conversaciones: d.conversaciones_activas || 0,
        citas: d.citas_hoy || 0,
        alertas: d.alertas || 0,
        tareas: d.tareas || 0,
        impersonation: d.impersonation_activas || 0,
        tests: d.tests_failed || 0,
      });
    } catch (e) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // Listener global de atajos "g + letra" estilo GitHub/Linear.
  // Press 'g' → entra en g-mode (1.5s) → press letra → navega.
  useEffect(() => {
    let timeoutId;
    const onKey = (e) => {
      // Ignorar si está escribiendo en un input/textarea/contenteditable
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      // Ignorar combinaciones con modificadores
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'g' && !gMode) {
        setGMode(true);
        timeoutId = setTimeout(() => setGMode(false), 1500);
        return;
      }
      if (gMode) {
        const key = (e.key || '').toLowerCase();
        const all = [
          ...menuItems,
          ...((isSuperAdmin && isPlataformaAdmin) ? adminItems : []),
        ];
        const target = all.find((it) => !it.divider && it.shortcut === key);
        if (target) {
          e.preventDefault();
          navigate(target.path);
        }
        setGMode(false);
        clearTimeout(timeoutId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timeoutId);
    };
  }, [gMode, isSuperAdmin, isPlataformaAdmin, navigate]);

  // Registrar página visitada en recientes (excluye dashboard y rutas no de menú).
  useEffect(() => {
    const path = location.pathname;
    if (!path || path === '/' || path === '/dashboard' || path === '/login') return;
    setRecentPaths((prev) => {
      const next = [path, ...prev.filter((p) => p !== path)].slice(0, 5);
      try { localStorage.setItem('sidebar_recents', JSON.stringify(next)); } catch {}
      return next;
    });
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  const isCollapsed = !isMobile && collapsed;

  const allItems = [
    ...menuItems,
    // Sección Admin: solo equipo NARA Tech (superadmin + company_id=4).
    // Los socios médicos cliente verán su propio admin de clínica más adelante.
    ...(isSuperAdmin && isPlataformaAdmin
      ? [{ divider: true, label: 'Admin' }, ...adminItems]
      : []),
  ];

  const renderIcon = (item) => {
    const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
    if (badgeCount > 0) {
      const color = item.badgeColor || 'error';  // error (rojo) | warning (ámbar) | info (azul) | primary
      return (
        <Badge badgeContent={badgeCount} color={color} max={99}>
          {item.icon}
        </Badge>
      );
    }
    return item.icon;
  };

  // Inicial(es) del usuario para el avatar
  const userInitials = (userName || userEmail || 'U')
    .split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // Etiqueta legible del rol
  const roleLabel = userRole === 'superadmin' ? 'Superadmin'
    : userRole === 'admin' ? 'Administrador'
    : userRole ? (userRole.charAt(0).toUpperCase() + userRole.slice(1))
    : 'Usuario';

  // Busca un path en el árbol de menús (inc. children) para mostrarlo en Recientes
  const findMenuItemByPath = (path) => {
    const haystack = [
      ...menuItems,
      ...((isSuperAdmin && isPlataformaAdmin) ? adminItems : []),
    ];
    for (const it of haystack) {
      if (it.divider) continue;
      if (it.path === path) return it;
      if (it.children) {
        for (const c of it.children) {
          if (c.path === path) return { ...c, parent: it.text };
        }
      }
    }
    return null;
  };

  // Items para la sección Recientes (solo los que existen en el menú).
  const recentsItems = recentPaths
    .map((p) => findMenuItemByPath(p))
    .filter((it) => it && it.path !== location.pathname)
    .slice(0, 4);

  // Items para la sección Favoritos.
  const favoriteItems = favoritePaths
    .map((p) => findMenuItemByPath(p))
    .filter(Boolean);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Branding compacto NARA Care */}
      <Box sx={{
        px: isCollapsed ? 1 : 2,
        py: 1.2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        gap: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        transition: 'padding 0.2s ease',
      }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 30, height: 30, objectFit: 'contain' }} />
        </Box>
        {!isCollapsed && (
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: 0.3 }}>
            NARA Care
          </Typography>
        )}
      </Box>

      {/* User card clickeable */}
      <Box
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isCollapsed ? 0 : 1.2,
          px: isCollapsed ? 1 : 2,
          py: 1.4,
          cursor: 'pointer',
          background: 'linear-gradient(180deg, rgba(30,58,95,0.04) 0%, rgba(30,58,95,0.0) 100%)',
          borderBottom: '1px solid #e2e8f0',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          transition: 'background 0.15s ease',
          '&:hover': { background: 'rgba(30, 58, 95, 0.06)' },
        }}
      >
        <Avatar sx={{
          width: 36, height: 36,
          bgcolor: '#1e3a5f', color: '#fff',
          fontSize: 14, fontWeight: 700,
          boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)',
          flexShrink: 0,
        }}>
          {userInitials}
        </Avatar>
        {!isCollapsed && (
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography sx={{
              color: '#0f172a', fontWeight: 700, fontSize: 13.5,
              lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {userName || 'Usuario'}
            </Typography>
            <Typography sx={{
              color: '#64748b', fontSize: 11, lineHeight: 1.3,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {empresaName || 'Sin empresa'}
            </Typography>
            {userRole && (
              <Typography sx={{
                display: 'inline-block', mt: 0.3,
                px: 0.7, py: 0.05, borderRadius: 0.6,
                bgcolor: userRole === 'superadmin' ? '#fef3c7' : '#e0e7ff',
                color: userRole === 'superadmin' ? '#92400e' : '#3730a3',
                fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}>
                {roleLabel}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Quick search trigger + Notificaciones */}
      <Box sx={{
        display: 'flex',
        flexDirection: isCollapsed ? 'column' : 'row',
        alignItems: 'stretch',
        gap: 0.5,
        mx: isCollapsed ? 0.5 : 1.5,
        my: 1,
      }}>
        <Box
          onClick={() => {
            const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true });
            window.dispatchEvent(ev);
          }}
          sx={{
            flex: 1,
            px: isCollapsed ? 0.5 : 1.2, py: 0.8,
            display: 'flex', alignItems: 'center', gap: 1,
            borderRadius: 2, cursor: 'pointer',
            bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
            color: '#64748b', fontSize: 12,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' },
            transition: 'all 0.15s',
          }}
        >
          <SearchIcon sx={{ fontSize: 16 }} />
          {!isCollapsed && (
            <>
              <Typography sx={{ fontSize: 12, flex: 1 }}>Buscar...</Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.3,
                px: 0.6, py: 0.05, borderRadius: 0.6,
                bgcolor: '#fff', border: '1px solid #cbd5e1',
                fontSize: 10, color: '#475569',
              }}>
                <KeyboardCommandKeyIcon sx={{ fontSize: 11 }} />K
              </Box>
            </>
          )}
        </Box>
        <Tooltip title={isCollapsed ? `Notificaciones${badges.alertas ? ` (${badges.alertas})` : ''}` : ''} placement="right" arrow>
          <IconButton
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            sx={{
              borderRadius: 2,
              bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
              color: '#64748b',
              alignSelf: isCollapsed ? 'center' : 'auto',
              width: isCollapsed ? 32 : 'auto',
              height: isCollapsed ? 32 : 'auto',
              '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' },
            }}
          >
            <Badge badgeContent={badges.alertas || 0} color="error" max={99}>
              <NotificationsIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Menu del user card */}
      <Menu
        anchorEl={userMenuAnchor}
        open={!!userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, mt: 0.5, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{userName}</Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>{userEmail}</Typography>
        </Box>
        <MenuItem
          onClick={() => { setUserMenuAnchor(null); navigate('/suscripcion'); }}
          sx={{ fontSize: 13 }}
        >
          Mi suscripción
        </MenuItem>
        <MenuItem
          onClick={() => { setUserMenuAnchor(null); navigate('/dashboard'); }}
          sx={{ fontSize: 13 }}
        >
          Dashboard
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={async () => {
            setUserMenuAnchor(null);
            try { await signOut(auth); } catch {}
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_email');
            localStorage.removeItem('user_name');
            navigate('/login');
          }}
          sx={{ fontSize: 13, color: '#dc2626' }}
        >
          Cerrar sesión
        </MenuItem>
      </Menu>

      {/* Sección Favoritos — solo expandido y con al menos 1 item */}
      {!isCollapsed && favoriteItems.length > 0 && (
        <Box sx={{
          mx: 1.5, mt: 1.2, mb: 0.5,
          px: 1, pt: 0.8, pb: 0.6,
          bgcolor: 'rgba(254, 243, 199, 0.4)',
          border: '1px solid #fde68a',
          borderRadius: 2,
          boxShadow: 'inset 0 1px 2px rgba(245, 158, 11, 0.05)',
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.6,
            px: 0.5, mb: 0.4,
          }}>
            <StarIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
            <Typography sx={{
              fontSize: 10, fontWeight: 700, color: '#92400e',
              textTransform: 'uppercase', letterSpacing: 1,
            }}>
              Favoritos
            </Typography>
          </Box>
          <List disablePadding>
            {favoriteItems.map((it) => {
              const sel = location.pathname === it.path || (it.path !== '/dashboard' && location.pathname.startsWith(it.path));
              return (
                <ListItem key={it.path} disablePadding sx={{ mb: 0.2 }}>
                  <ListItemButton
                    onClick={() => { navigate(it.path); setMobileOpen(false); }}
                    sx={{
                      borderRadius: 1.5, py: 0.3, px: 0.8,
                      bgcolor: sel ? 'rgba(245, 158, 11, 0.18)' : 'transparent',
                      '&:hover': { bgcolor: sel ? 'rgba(245, 158, 11, 0.25)' : 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: sel ? '#92400e' : '#94a3b8',
                      minWidth: 24, justifyContent: 'center',
                      '& svg': { fontSize: 15 },
                    }}>
                      {it.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={it.text}
                      secondary={it.parent || null}
                      sx={{
                        m: 0,
                        '& .MuiListItemText-primary': { fontSize: 12, color: sel ? '#92400e' : '#475569', fontWeight: sel ? 700 : 500 },
                        '& .MuiListItemText-secondary': { fontSize: 9.5, color: '#cbd5e1' },
                      }}
                    />
                    <Tooltip title="Quitar de favoritos" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(it.path); }}
                        sx={{ p: 0.2, color: '#f59e0b', '&:hover': { color: '#dc2626' } }}
                      >
                        <StarIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* Sección Recientes — solo expandido y con al menos 2 items */}
      {!isCollapsed && recentsItems.length >= 2 && (
        <Box sx={{
          mx: 1.5, mt: 1.2, mb: 0.5,
          px: 1, pt: 0.8, pb: 0.6,
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.6,
            px: 0.5, mb: 0.4,
          }}>
            <HistoryIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
            <Typography sx={{
              fontSize: 10, fontWeight: 700, color: '#94a3b8',
              textTransform: 'uppercase', letterSpacing: 1,
            }}>
              Recientes
            </Typography>
          </Box>
          <List disablePadding>
            {recentsItems.map((it) => (
              <ListItem key={it.path} disablePadding sx={{ mb: 0.2 }}>
                <ListItemButton
                  onClick={() => { navigate(it.path); setMobileOpen(false); }}
                  sx={{
                    borderRadius: 1.5, py: 0.3, px: 0.8,
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                  }}
                >
                  <ListItemIcon sx={{
                    color: '#94a3b8', minWidth: 24, justifyContent: 'center',
                    '& svg': { fontSize: 15 },
                  }}>
                    {it.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={it.text}
                    secondary={it.parent || null}
                    sx={{
                      m: 0,
                      '& .MuiListItemText-primary': { fontSize: 12, color: '#64748b', fontWeight: 500 },
                      '& .MuiListItemText-secondary': { fontSize: 9.5, color: '#cbd5e1' },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <List sx={{ flex: 1, px: isCollapsed ? 0.5 : 1.5, pt: 1.5, overflowY: 'auto' }}>
        {allItems.map((item, idx) => {
          if (item.divider) {
            return isCollapsed ? (
              <Divider key={`div-${idx}`} sx={{ my: 1, mx: 0.5 }} />
            ) : (
              <Box key={`div-${idx}`} sx={{ mt: 1.2, mb: 0.4, px: 1 }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  {item.label}
                </Typography>
              </Box>
            );
          }

          // Item con children (submenú). Cuando el sidebar está expandido se
          // muestra como grupo colapsable. Cuando está colapsado, los hijos se
          // listan directamente como ítems planos para no perder funcionalidad.
          if (item.children && item.children.length > 0) {
            const groupKey = `grp-${item.text}`;
            const childMatchPath = (p) => location.pathname === p || location.pathname.startsWith(p + '/');
            const childActive = item.children.some((c) => childMatchPath(c.path));
            const isOpen = isCollapsed
              ? false
              : (openGroups[groupKey] !== undefined ? openGroups[groupKey] : childActive);

            if (isCollapsed) {
              // En modo colapsado, mostrar los hijos como ítems individuales con tooltip.
              return item.children.map((child) => {
                const selected = childMatchPath(child.path);
                return (
                  <Tooltip key={child.text} title={`${item.text} · ${child.text}`} placement="right" arrow>
                    <ListItem disablePadding sx={{ mb: 0.4 }}>
                      <ListItemButton
                        onClick={() => { navigate(child.path); setMobileOpen(false); }}
                        sx={{
                          borderRadius: 2,
                          bgcolor: selected ? 'rgba(30, 58, 95, 0.08)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
                          justifyContent: 'center',
                          px: 1, py: 0.6,
                        }}
                      >
                        <ListItemIcon sx={{
                          color: selected ? '#1e3a5f' : '#94a3b8',
                          minWidth: 0, justifyContent: 'center',
                          '& svg': { fontSize: 20 },
                        }}>
                          {child.icon}
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              });
            }

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.4 }}>
                  <ListItemButton
                    onClick={() => toggleGroup(groupKey)}
                    sx={{
                      borderRadius: 2,
                      bgcolor: childActive ? 'rgba(30, 58, 95, 0.04)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
                      px: 1.5, py: 0.6,
                    }}
                  >
                    <ListItemIcon sx={{
                      color: childActive ? '#1e3a5f' : '#94a3b8',
                      minWidth: 32, justifyContent: 'center',
                      '& svg': { fontSize: 20 },
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: childActive ? 600 : 500,
                          color: childActive ? '#1e3a5f' : '#475569',
                          fontSize: 13,
                        }
                      }}
                    />
                    {isOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => {
                      const selected = childMatchPath(child.path);
                      return (
                        <ListItem key={child.text} disablePadding sx={{ mb: 0.4, position: 'relative' }}>
                          {selected && (
                            <Box sx={{
                              position: 'absolute',
                              left: 20, top: 4, bottom: 4,
                              width: 3, borderRadius: 2,
                              background: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)',
                              boxShadow: '0 0 6px rgba(251, 191, 36, 0.45)',
                              zIndex: 1,
                            }} />
                          )}
                          <ListItemButton
                            onClick={() => { navigate(child.path); setMobileOpen(false); }}
                            sx={{
                              borderRadius: 2,
                              ml: 2.5,
                              bgcolor: selected ? 'rgba(30, 58, 95, 0.10)' : 'transparent',
                              '&:hover': { bgcolor: selected ? 'rgba(30, 58, 95, 0.13)' : 'rgba(30, 58, 95, 0.05)' },
                              px: 1.5, py: 0.4,
                              pl: selected ? 1.8 : 1.5,
                              transition: 'padding-left 0.15s ease, background 0.15s ease',
                            }}
                          >
                            <ListItemIcon sx={{
                              color: selected ? '#1e3a5f' : '#94a3b8',
                              minWidth: 28,
                              justifyContent: 'center',
                              '& svg': {
                                fontSize: 16,
                                ...(selected ? { filter: 'drop-shadow(0 1px 2px rgba(30, 58, 95, 0.3))' } : {}),
                              },
                            }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.text}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontWeight: selected ? 700 : 500,
                                  color: selected ? '#1e3a5f' : '#64748b',
                                  fontSize: 12.5,
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          const selected = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

          const isHovered = hoveredItem === item.path;
          const fav = isFavorite(item.path);
          const button = (
            <ListItem
              key={item.text}
              disablePadding
              sx={{ mb: 0.4, position: 'relative' }}
              onMouseEnter={() => !isCollapsed && setHoveredItem(item.path)}
              onMouseLeave={() => setHoveredItem((v) => (v === item.path ? null : v))}
            >
              {/* Barra vertical de color en el item activo (indicador fuerte) */}
              {selected && (
                <Box sx={{
                  position: 'absolute',
                  left: 0, top: 5, bottom: 5,
                  width: 3, borderRadius: 2,
                  background: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)',
                  boxShadow: '0 0 8px rgba(251, 191, 36, 0.5)',
                  zIndex: 1,
                }} />
              )}
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2,
                  bgcolor: selected ? 'rgba(30, 58, 95, 0.10)' : 'transparent',
                  '&:hover': { bgcolor: selected ? 'rgba(30, 58, 95, 0.13)' : 'rgba(30, 58, 95, 0.05)' },
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  px: isCollapsed ? 1 : 1.5,
                  py: 0.6,
                  pr: isCollapsed ? 1 : 0.5,
                  ml: selected ? 0.6 : 0,
                  transition: 'margin-left 0.15s ease, background 0.15s ease',
                }}
              >
                <ListItemIcon sx={{
                  color: selected ? '#1e3a5f' : '#94a3b8',
                  minWidth: isCollapsed ? 0 : 32,
                  justifyContent: 'center',
                  '& svg': {
                    fontSize: 20,
                    ...(selected ? { filter: 'drop-shadow(0 1px 2px rgba(30, 58, 95, 0.3))' } : {}),
                  },
                }}>
                  {renderIcon(item)}
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: selected ? 700 : 500,
                          color: selected ? '#1e3a5f' : '#475569',
                          fontSize: 13,
                        }
                      }}
                    />
                    {/* Acciones a la derecha: shortcut + pin + quickAdd */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4,
                      flexShrink: 0,
                    }}>
                      {/* Shortcut como dos kbd boxes — visible solo en hover o g-mode */}
                      {item.shortcut && (isHovered || gMode) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          {['g', item.shortcut].map((k, i) => (
                            <Box
                              key={i}
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 16, height: 16,
                                px: 0.4,
                                borderRadius: 0.6,
                                bgcolor: gMode ? '#fbbf24' : '#fff',
                                border: `1px solid ${gMode ? '#f59e0b' : '#cbd5e1'}`,
                                fontSize: 9.5, fontWeight: 700,
                                color: gMode ? '#92400e' : '#475569',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                boxShadow: gMode
                                  ? '0 0 6px rgba(251, 191, 36, 0.4)'
                                  : '0 1px 0 rgba(0,0,0,0.05)',
                                textTransform: 'uppercase',
                                lineHeight: 1,
                              }}
                            >
                              {k}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {/* Pin (favorito) — visible si está hovered o ya es favorito */}
                      {(isHovered || fav) && (
                        <Tooltip title={fav ? 'Quitar de favoritos' : 'Marcar favorito'} placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(item.path); }}
                            sx={{
                              p: 0.3,
                              color: fav ? '#f59e0b' : '#cbd5e1',
                              '&:hover': { color: fav ? '#dc2626' : '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.08)' },
                            }}
                          >
                            {fav
                              ? <StarIcon sx={{ fontSize: 14 }} />
                              : <StarBorderIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Quick add — visible solo en hover si el item lo soporta */}
                      {isHovered && item.quickAdd && (
                        <Tooltip title={item.quickAddLabel || 'Crear'} placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(item.quickAdd); setMobileOpen(false); }}
                            sx={{
                              p: 0.3, color: '#1e3a5f',
                              bgcolor: 'rgba(30, 58, 95, 0.08)',
                              '&:hover': { bgcolor: '#1e3a5f', color: '#fff' },
                              transition: 'all 0.15s',
                            }}
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
          );

          return isCollapsed ? (
            <Tooltip key={item.text} title={item.text} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={item.text}>{button}</React.Fragment>
          );
        })}
      </List>

      {/* Toggle button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5, borderTop: '1px solid #e2e8f0' }}>
          <IconButton onClick={toggleCollapsed} size="small" sx={{ color: '#94a3b8' }}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}

      {/* Footer del sidebar — solo si viene del Hub mostrar "Volver" */}
      {localStorage.getItem('hub_origin') === 'true' && (
        <Box sx={{ p: isCollapsed ? 0.5 : 1.5, borderTop: '1px solid #e2e8f0' }}>
          <Tooltip title={isCollapsed ? 'Volver al Hub' : ''} placement="right" arrow>
            <ListItemButton
              onClick={() => { window.location.href = HUB_URL; }}
              sx={{
                borderRadius: 2,
                py: 0.6,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : 1.5,
              }}
            >
              <ListItemIcon sx={{
                color: '#1e3a5f',
                minWidth: isCollapsed ? 0 : 32,
                justifyContent: 'center',
                '& svg': { fontSize: 20 },
              }}>
                <HubIcon />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText primary="Volver al Hub"
                  sx={{ '& .MuiListItemText-primary': { fontSize: 13, color: '#1e3a5f', fontWeight: 500 } }} />
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  // Lista de comandos para el palette: aplana menú principal + admin (si aplica),
  // expandiendo grupos colapsables (children) en items individuales.
  const commands = [];
  for (const it of menuItems) {
    if (it.divider) continue;
    commands.push({
      label: it.text,
      path: it.path,
      icon: it.icon,
      group: 'Menú principal',
      keywords: [it.text],
    });
  }
  if (isSuperAdmin && isPlataformaAdmin) {
    for (const it of adminItems) {
      if (it.divider) continue;
      if (it.children) {
        for (const child of it.children) {
          commands.push({
            label: child.text,
            path: child.path,
            icon: child.icon,
            group: `Admin · ${it.text}`,
            keywords: [it.text, child.text],
          });
        }
      } else {
        commands.push({
          label: it.text,
          path: it.path,
          icon: it.icon,
          group: 'Admin',
          keywords: [it.text],
        });
      }
    }
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      <CommandPalette commands={commands} />
      <NotificationsPopover
        anchorEl={notifAnchor}
        open={!!notifAnchor}
        onClose={() => setNotifAnchor(null)}
        onChange={fetchBadges}
      />
      {/* Indicador g-mode activo */}
      {gMode && (
        <Box sx={{
          position: 'fixed',
          bottom: 24, left: '50%', transform: 'translateX(-50%)',
          px: 2.5, py: 1,
          bgcolor: '#1e3a5f', color: '#fff',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(30, 58, 95, 0.3)',
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 1,
          zIndex: 9999,
          animation: 'slideUp 0.18s ease',
        }}>
          <Box component="kbd" sx={{
            px: 0.7, py: 0.05, borderRadius: 0.5,
            bgcolor: '#fbbf24', color: '#1e3a5f',
            fontSize: 11, fontWeight: 700,
          }}>g</Box>
          + presiona una letra
          <Typography sx={{ fontSize: 11, color: '#cbd5e1', ml: 0.5 }}>
            (d, a, e, y, f, s, b, o, m)
          </Typography>
        </Box>
      )}
      {isMobile && (
        <AppBar position="fixed" sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          boxShadow: 'none',
        }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%', mx: 1,
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </Box>
            <Typography sx={{ fontWeight: 700 }}>NARA Care</Typography>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s ease',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            bgcolor: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
            transition: 'width 0.2s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box sx={{
        flex: 1,
        mt: isMobile ? 8 : 0,
        maxWidth: '100%',
        overflow: 'auto',
        transition: 'margin-left 0.2s ease',
      }}>
        {children}
      </Box>
    </Box>
  );
}
